<template>
  <div class="container">
    <div class="center_out h-full">
      <div class="card login-card">
        <div class="login-card-1" id="login-card-1"></div>
        <div class="login-card-2">
          <img src="/images/logo.svg" alt="" />
          <div class="card-inner">
            <p>Bitte melden Sie sich am Portal an:</p>

            <!-- Step 1: Email Input -->
            <form v-if="step === 1" action="" @submit.prevent="checkLoginType">
              <div class="from-group mt-2">
                <input
                  type="email"
                  class="form-field"
                  placeholder="Email-Adresse"
                  v-model="email"
                  required
                />
              </div>

              <div class="mt-4">
                <vs-alert :active.sync="showError" closable close-icon="close"
                  >{{ errorMessage }}
                </vs-alert>
              </div>

              <div class="lower-con">
                <button type="submit" class="from-btn" :disabled="loading">
                  <span v-if="loading">Laden...</span>
                  <span v-else>Weiter</span>
                </button>
              </div>
            </form>

            <!-- Step 2: Password Input (for local login) -->
            <form
              v-if="step === 2"
              action=""
              @submit.prevent="submitLocalLogin"
            >
              <div class="from-group mt-2">
                <input
                  type="email"
                  class="form-field"
                  placeholder="Email-Adresse"
                  v-model="email"
                  readonly
                  disabled
                />
              </div>
              <div class="from-group mt-4">
                <input
                  type="password"
                  class="form-field"
                  placeholder="Passwort"
                  v-model="password"
                  required
                  ref="passwordField"
                />
              </div>

              <div class="mt-4">
                <vs-alert :active.sync="showError" closable close-icon="close"
                  >{{ errorMessage }}
                </vs-alert>
              </div>

              <div class="lower-con">
                <button type="submit" class="from-btn" :disabled="loading">
                  <span v-if="loading">Anmelden...</span>
                  <span v-else>Anmelden</span>
                </button>
                <div class="mt-4 login-links">
                  <a href="#" @click.prevent="resetFlow" class="router-link"
                    >← Zurück</a
                  >
                  <router-link
                    class="router-link"
                    :to="{ name: 'ResetPassword' }"
                    >Passwort vergessen?
                  </router-link>
                </div>
              </div>
            </form>

            <!-- Step 3: Redirecting to Keycloak -->
            <div v-if="step === 3" class="text-center">
              <div class="from-group mt-4">
                <p>Weiterleitung zu Keycloak...</p>
                <div class="spinner mt-3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <small class="version mt-3">Version: {{ version }}</small>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
  name: "Login",

  data() {
    return {
      step: 1, // 1 = email input, 2 = password input (local), 3 = redirecting (keycloak)
      email: "",
      password: "",
      showError: false,
      errorMessage: "",
      loading: false,
    };
  },
  mounted() {
    const emailFromQuery = this.$route.query.email;
    if (emailFromQuery) {
      this.email = emailFromQuery;
    }
  },
  computed: {
    version() {
      if (GIT_DESCRIBE !== null) {
        return GIT_DESCRIBE.tag + " - " + GIT_DESCRIBE.hash;
      } else {
        return "no-version";
      }
    },
  },

  methods: {
    /**
     * Step 1: Check login type for entered email
     */
    async checkLoginType() {
      if (!this.email || this.email.trim() === "") {
        this.$vs.notify({
          color: "danger",
          position: "bottom-right",
          title: "Bitte geben Sie Ihre Email-Adresse ein!",
        });
        return;
      }

      this.loading = true;
      this.showError = false;

      try {
        const loginType = await this.$store.dispatch(
          "auth/getLoginType",
          this.email,
        );

        if (loginType === "local") {
          // Show password field for local login
          this.step = 2;
          this.$nextTick(() => {
            // Focus password field
            if (this.$refs.passwordField) {
              this.$refs.passwordField.focus();
            }
          });
        } else if (loginType === "keycloak_oauth2") {
          // Prüfen ob bereits eine aktive Keycloak-Session existiert
          const restored = await this.$store.dispatch(
            "auth/tryRestoreKeycloakSession",
          );
          if (restored) {
            const everLoggedIn = localStorage.getItem("bos_ever_logged_in");
            const logoutClean = localStorage.getItem("bos_logout_clean");
            const showWarning = !!(everLoggedIn && !logoutClean);
            localStorage.removeItem("bos_logout_clean");
            localStorage.setItem("bos_ever_logged_in", "1");
            this.$router.push({ path: "/dashboard", query: showWarning ? { session_warning: "1" } : {} });
            return;
          }
          // Keine aktive Session – zu Keycloak weiterleiten
          this.step = 3;
          await this.$store.dispatch("auth/initiateKeycloakLogin", this.email);
        } else {
          throw new Error("Unbekannter Login-Typ");
        }
      } catch (error) {
        console.error("Login type check error:", error);

        // Check for 404 (user not found)
        if (error.response && error.response.status === 404) {
          this.$vs.notify({
            color: "danger",
            position: "bottom-right",
            title: "Konto nicht gefunden!",
            text: "Bitte überprüfen Sie Ihre Email-Adresse.",
          });
        } else {
          this.$vs.notify({
            color: "danger",
            position: "bottom-right",
            title: "Fehler beim Login!",
            text: "Bitte versuchen Sie es erneut.",
          });
        }
      } finally {
        this.loading = false;
      }
    },

    /**
     * Step 2: Submit local login with password
     */
    async submitLocalLogin() {
      if (!this.password || this.password.trim() === "") {
        this.$vs.notify({
          color: "danger",
          position: "bottom-right",
          title: "Bitte geben Sie Ihr Passwort ein!",
        });
        return;
      }

      this.loading = true;
      this.showError = false;

      const status = await this.$store.dispatch("auth/createLogin", {
        email: this.email,
        password: this.password,
      });

      if (await this.$store.dispatch("auth/isAuthenticated")) {
        this.$vs.notify({
          color: "success",
          position: "bottom-right",
          title: "Willkommen im BOS-ID Portal",
        });
        const everLoggedIn = localStorage.getItem("bos_ever_logged_in");
        const logoutClean = localStorage.getItem("bos_logout_clean");
        const showWarning = !!(everLoggedIn && !logoutClean);
        localStorage.removeItem("bos_logout_clean");
        localStorage.setItem("bos_ever_logged_in", "1");
        this.$router.push({ path: "/dashboard", query: showWarning ? { session_warning: "1" } : {} });
      } else {
        this.$vs.notify({
          color: "danger",
          position: "bottom-right",
          title: "Anmeldung fehlgeschlagen!",
          text: "Bitte überprüfen Sie Ihre Zugangsdaten.",
        });
        this.loading = false;
      }
    },

    /**
     * Reset flow back to step 1
     */
    resetFlow() {
      this.step = 1;
      this.password = "";
      this.showError = false;
      this.errorMessage = "";
    },
  },
});
</script>

<style scoped>
#login-card-1 {
  background: url("../../public/images/login-image.png");
  min-height: 100%;
  max-height: 30vh;
  background-repeat: no-repeat;
  background-position: center 25%;
  -webkit-background-size: 100%;
  -moz-background-size: 100%;
  -o-background-size: 100%;
  background-size: 100%;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}

.version {
  font-size: 0.75em;
  color: rgb(202, 202, 202);
}

.login-links a + a {
  margin-left: 1.5rem;
}
</style>
